/**
 * Conexiones WebSocket — operación y tienda.
 *
 * ─── Qué resuelve esta implementación ────────────────────────────────────────
 *
 * **H1 — no había manejo de errores ni reconexión.** No se asignaban `onerror`
 * ni `onclose`, las acciones de fallo estaban declaradas pero nunca se
 * despachaban, y no había reintentos. Si el socket caía —corte de red, reinicio
 * del servidor, portátil suspendido, timeout de proxy— el dashboard se
 * congelaba en silencio: los couriers dejaban de moverse en el mapa y el
 * operador no tenía ninguna señal de estar mirando datos viejos.
 *
 * **H2 — sockets duplicados.** `wsEvents()` se invoca desde `MainContainer`,
 * `DashboardContainer` y `OldDashboardContainer`. Cada llamada sobrescribía la
 * variable de módulo, así que entrar a `/dashboard` abría un segundo socket y
 * dejaba huérfano al primero: seguía recibiendo y despachando, duplicando cada
 * actualización, y `closeWS` ya no podía cerrarlo. Con el doble montaje de
 * `StrictMode` (Fase 7) serían cuatro.
 *
 * ─── Diseño ─────────────────────────────────────────────────────────────────
 *
 * Los dos sockets comparten toda la lógica, así que se extrae a una fábrica
 * (`crearCanal`) en vez de duplicarla. La API pública no cambia: los
 * consumidores siguen usando `wsEvents`, `wsEventsShop`, `sendMessage`,
 * `closeWS` y `closeWSShop`.
 */

// ─── Socket de operación ─────────────────────────────────────────────────────
import entorno from '../config/entorno'

export const WS_CONNECT = 'WS_CONNECT'
export const WS_CONNECT_SUCCESS = 'WS_CONNECT_SUCCESS'
export const WS_CONNECT_FAILURE = 'WS_CONNECT_FAILURE'
export const WS_MESSAGE = 'WS_MESSAGE'
export const WS_RECONNECTING = 'WS_RECONNECTING'
/**
 * Se despacha cuando la conexión vuelve **tras haberse caído** — nunca en la
 * conexión inicial. Es la señal para que los consumidores reconsulten por HTTP
 * lo que se perdieron durante el corte (D2).
 */
export const WS_RECONNECTED = 'WS_RECONNECTED'
export const WS_CLOSE = 'WS_CLOSE'

// ─── Socket de tienda ────────────────────────────────────────────────────────
export const WS_CONNECT_SHOP_REQUEST = 'WS_CONNECT_SHOP_REQUEST'
export const WS_CONNECT_SHOP_SUCCESS = 'WS_CONNECT_SHOP_SUCCESS'
export const WS_CONNECT_SHOP_FAILURE = 'WS_CONNECT_SHOP_FAILURE'
export const WS_MESSAGE_SHOP = 'WS_MESSAGE_SHOP'
export const WS_RECONNECTING_SHOP = 'WS_RECONNECTING_SHOP'
export const WS_RECONNECTED_SHOP = 'WS_RECONNECTED_SHOP'
export const WS_CLOSE_SHOP = 'WS_CLOSE_SHOP'

/**
 * Política de reintentos.
 *
 * Retroceso exponencial con tope y *jitter*. El jitter evita que todos los
 * navegadores abiertos reintenten en el mismo instante tras una caída del
 * servidor y lo tumben otra vez al volver.
 *
 * ─── Por qué hay dos políticas y no una ─────────────────────────────────────
 *
 * Un canal que **estuvo conectado** y se cayó reintenta **indefinidamente**:
 * es un dashboard de operación que puede quedarse abierto toda la jornada, y
 * rendirse significaría que la pantalla nunca vuelve sola aunque la red se
 * recupere. El tope de 30 s hace que sea barato.
 *
 * Un canal que **nunca llegó a conectar** se rinde tras `intentosSinConectar`.
 * La diferencia importa: si el servicio no está desplegado en este entorno —el
 * socket de tienda no lo está en sandbox— reintentar para siempre no lo va a
 * arreglar, y el navegador escribe un error rojo en consola por cada intento
 * que no se puede silenciar desde JavaScript. Eso llena la consola de ruido y
 * entierra los errores que sí importan, que es exactamente lo que pasó en la
 * validación previa a la Fase 5.
 *
 * Seis intentos son ~1 minuto: de sobra para distinguir «el servicio tarda en
 * levantar» de «el servicio no existe aquí».
 */
export const POLITICA_RECONEXION = {
  esperaInicialMs: 1000,
  esperaMaximaMs: 30000,
  factor: 2,
  jitterMs: 500,
  intentosSinConectar: 6
}

/** Calcula la espera del siguiente intento (base 1s, 2s, 4s… con tope). */
export const calcularEspera = (intento, politica = POLITICA_RECONEXION) => {
  const { esperaInicialMs, esperaMaximaMs, factor, jitterMs } = politica
  const base = Math.min(
    esperaInicialMs * Math.pow(factor, Math.max(0, intento - 1)),
    esperaMaximaMs
  )
  return base + Math.floor(Math.random() * jitterMs)
}

/**
 * Crea un canal WebSocket gestionado.
 *
 * @param {object} cfg
 * @param {Function} cfg.construirUrl Devuelve la URL, leyendo el token vigente.
 * @param {object}   cfg.acciones     Tipos a despachar en cada transición.
 * @param {boolean}  cfg.conCola      Si admite encolar envíos previos a abrir.
 */
const crearCanal = ({ construirUrl, acciones, conCola = false }) => {
  let socket = null
  let dispatchActual = null
  let temporizador = null
  let intentos = 0
  let cierreDeliberado = false
  let cola = []
  /** Distingue «se cayó» de «nunca llegó a funcionar». */
  let conectoAlgunaVez = false
  /** Se agotaron los intentos sin conectar nunca: no se reintenta más solo. */
  let rendido = false

  const estaVivo = () =>
    Boolean(
      socket &&
      (socket.readyState === WebSocket.CONNECTING ||
        socket.readyState === WebSocket.OPEN)
    )

  const cancelarReintento = () => {
    if (temporizador) {
      clearTimeout(temporizador)
      temporizador = null
    }
  }

  const programarReintento = () => {
    // Sin sesión no tiene sentido reintentar: el backend rechazaría el token.
    if (!localStorage.getItem('accessToken')) return
    if (cierreDeliberado || temporizador || rendido) return

    intentos += 1

    // Un canal que nunca conectó se rinde: si el servicio no está desplegado
    // en este entorno, seguir intentando sólo llena la consola de errores
    // rojos del navegador —uno por intento, imposibles de silenciar desde
    // JavaScript— y entierra los que sí importan.
    if (
      !conectoAlgunaVez &&
      intentos > POLITICA_RECONEXION.intentosSinConectar
    ) {
      rendido = true
      if (dispatchActual) {
        dispatchActual({
          type: acciones.fallo,
          error: `Servicio no disponible tras ${POLITICA_RECONEXION.intentosSinConectar} intentos`,
          rendido: true
        })
      }
      return
    }

    const espera = calcularEspera(intentos)

    if (dispatchActual) {
      dispatchActual({
        type: acciones.reconectando,
        intento: intentos,
        esperaMs: espera
      })
    }

    temporizador = setTimeout(() => {
      temporizador = null
      abrir(dispatchActual)
    }, espera)
  }

  /**
   * Reintenta **ya**, sin esperar al temporizador.
   *
   * Se usa cuando el navegador avisa de que la red volvió. Sin esto, tras un
   * corte largo el retroceso está en su tope y la pantalla puede tardar hasta
   * 30 s en revivir aunque la conexión ya esté de vuelta — que es justo lo que
   * se observó en la validación: la franja de aviso no desaparecía al volver a
   * poner el navegador *online*.
   */
  const reintentarAhora = () => {
    if (cierreDeliberado || estaVivo() || !dispatchActual) return
    cancelarReintento()
    // La red volvió: aunque nos hubiéramos rendido, merece un intento más.
    rendido = false
    abrir(dispatchActual)
  }

  function abrir(dispatch) {
    // Idempotente: si ya hay un socket conectando o abierto, no se crea otro.
    // Es lo que evita los sockets huérfanos cuando varios contenedores llaman
    // a `wsEvents()` al montarse (H2).
    if (estaVivo()) return

    dispatchActual = dispatch
    cierreDeliberado = false
    dispatch({ type: acciones.conectando })

    try {
      socket = new WebSocket(construirUrl())
    } catch (error) {
      dispatch({ type: acciones.fallo, error: String(error) })
      programarReintento()
      return
    }

    socket.onopen = () => {
      // Se captura antes de reiniciar el contador: distingue una reconexión
      // de la conexión inicial.
      const veniaDeUnCorte = intentos > 0

      intentos = 0
      conectoAlgunaVez = true
      rendido = false
      cancelarReintento()
      dispatch({ type: acciones.exito })

      if (conCola) {
        while (cola.length > 0) socket.send(JSON.stringify(cola.shift()))
      }

      // Mientras el socket estuvo caído no llegó ningún evento, así que el
      // estado en pantalla tiene un hueco. Esta señal pide a los consumidores
      // que reconsulten por HTTP y lo cierren (D2).
      if (veniaDeUnCorte) {
        dispatch({ type: acciones.reconectado })
      }
    }

    socket.onmessage = evento => {
      try {
        dispatch({ type: acciones.mensaje, data: JSON.parse(evento.data) })
      } catch (_) {
        // Un mensaje corrupto no debe tumbar la conexión ni el dashboard.
      }
    }

    socket.onerror = () => {
      // `onerror` no trae detalle por seguridad del navegador. El cierre que
      // viene detrás es quien dispara el reintento.
      dispatch({ type: acciones.fallo, error: 'Error de conexión' })
    }

    socket.onclose = evento => {
      if (cierreDeliberado) return

      dispatch({
        type: acciones.fallo,
        error: 'Conexión perdida',
        code: evento && evento.code
      })
      programarReintento()
    }
  }

  return {
    abrir,
    reintentarAhora,

    cerrar: (dispatch, entidad) => {
      cierreDeliberado = true
      cancelarReintento()
      intentos = 0
      rendido = false
      conectoAlgunaVez = false
      cola = []

      dispatch({ type: acciones.cierre, entity: entidad })

      if (socket) {
        // Se sueltan los manejadores antes de cerrar para que el `onclose`
        // resultante no programe un reintento.
        socket.onopen = null
        socket.onmessage = null
        socket.onerror = null
        socket.onclose = null
        socket.close()
        socket = null
      }
    },

    enviar: mensaje => {
      if (socket && socket.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify(mensaje))
      } else if (conCola) {
        cola.push(mensaje)
      }
    },

    /** Sólo para tests: devuelve el estado interno del canal. */
    _estado: () => ({
      intentos,
      cierreDeliberado,
      hayTemporizador: Boolean(temporizador),
      enCola: cola.length,
      vivo: estaVivo(),
      conectoAlgunaVez,
      rendido
    })
  }
}

const canalOperacion = crearCanal({
  conCola: true,
  construirUrl: () =>
    `${entorno.ws}/ws/admin/?token=${localStorage.getItem('accessToken')}`,
  acciones: {
    conectando: WS_CONNECT,
    exito: WS_CONNECT_SUCCESS,
    fallo: WS_CONNECT_FAILURE,
    mensaje: WS_MESSAGE,
    reconectando: WS_RECONNECTING,
    reconectado: WS_RECONNECTED,
    cierre: WS_CLOSE
  }
})

const canalTienda = crearCanal({
  construirUrl: () =>
    `${entorno.wsTienda}/stream/?token=${localStorage.getItem('accessToken')}`,
  acciones: {
    conectando: WS_CONNECT_SHOP_REQUEST,
    exito: WS_CONNECT_SHOP_SUCCESS,
    fallo: WS_CONNECT_SHOP_FAILURE,
    mensaje: WS_MESSAGE_SHOP,
    reconectando: WS_RECONNECTING_SHOP,
    reconectado: WS_RECONNECTED_SHOP,
    cierre: WS_CLOSE_SHOP
  }
})

/**
 * Cuando el navegador avisa de que la red volvió, se reintenta **al instante**
 * en vez de esperar al temporizador.
 *
 * Sin esto, tras un corte de varios minutos el retroceso está en su tope y la
 * pantalla tarda hasta 30 s en revivir aunque la conexión ya esté de vuelta.
 * En la validación previa a la Fase 5 se leyó como «la franja no desaparece
 * sola»: sí desaparecía, pero mucho después de que el operador diera por
 * recuperada la red.
 *
 * El evento `online` del navegador es exactamente esa señal, y es gratis.
 */
export const conectarAvisosDeRed = () => {
  if (typeof window === 'undefined' || !window.addEventListener)
    return () => {}

  const alVolverLaRed = () => {
    canalOperacion.reintentarAhora()
    canalTienda.reintentarAhora()
  }

  window.addEventListener('online', alVolverLaRed)
  return () => window.removeEventListener('online', alVolverLaRed)
}

// ─── API pública (sin cambios para los consumidores) ─────────────────────────

export function wsEvents() {
  return dispatch => canalOperacion.abrir(dispatch)
}

export function closeWS(type) {
  return dispatch => canalOperacion.cerrar(dispatch, type)
}

export function sendMessage(message) {
  canalOperacion.enviar(message)
}

export function wsEventsShop() {
  return dispatch => canalTienda.abrir(dispatch)
}

export function closeWSShop(type) {
  return dispatch => canalTienda.cerrar(dispatch, type)
}

/** Sólo para tests. */
export const _canales = { operacion: canalOperacion, tienda: canalTienda }
